import useSpacesList from 'hooks/useSpacesList';
import { useWindowSize } from 'hooks/useWindowSize';
import { SkeletonLine } from 'koba/components/SkeletonPost';
import { useTranslation } from 'react-i18next';
import { SCREEN_LG } from 'utils/constants';

import React, { useMemo } from 'react';

import CommunityChecklist from 'components/CommunityChecklist';
import { useCommunityContext } from 'components/CommunityContext';
import CreateNewPost from 'components/CreateNewPost';
import UpcomingEvents from 'components/Events/components/UpcomingEvents';
import HomePosts from 'components/HomePosts/HomePosts';
import SpaceCardsList from 'components/SpaceCardsList';
import VisibilityCloak from 'components/VisibilityCloak/VisibilityCloak';
import FeatureCloak from 'components/VisibilityHelpers/FeatureCloak';
import AvatarBanner from 'components/new/AvatarBanner/AvatarBanner';
import Hero from 'components/new/layout/Hero/Hero';

interface HomeFeedProps {
  community: {
    published: boolean;
    primaryColor: string;
    description: string;
    imageUrl?: string;
    name: string;
  };
  postListState: {
    useCacheFirst: boolean;
  };
  firstName?: string;
}

const HomeFeed = ({ community, postListState, firstName }: HomeFeedProps) => {
  const { width } = useWindowSize();
  const isTabletOrSmaller = width < SCREEN_LG;
  const { currentUser } = useCommunityContext();
  const { t } = useTranslation();
  const { firstPostableSpace, isSpacesListLoading } = useSpacesList();

  const shouldShowCommunitiesChecklist = !community.published && isTabletOrSmaller;

  const renderPostSection = useMemo(() => {
    if (isSpacesListLoading) {
      return <SkeletonLine height="80px" width="100%" />;
    }

    if (firstPostableSpace) {
      return <CreateNewPost />;
    }

    return (
      <AvatarBanner
        message={t(
          'components-homeFeed-all_spaces_read_only',
          'All spaces are currently set to read only. Only moderators can post'
        )}
        user={currentUser}
      />
    );
  }, [isSpacesListLoading, firstPostableSpace, t, currentUser]);

  return (
    <>
      <Hero description={community.description} firstName={firstName} src={community.imageUrl} />
      {renderPostSection}
      {shouldShowCommunitiesChecklist && (
        <VisibilityCloak admins>
          <CommunityChecklist />
        </VisibilityCloak>
      )}
      <FeatureCloak communityEvents>
        <UpcomingEvents />
      </FeatureCloak>

      <HomePosts postListState={postListState} />

      <SpaceCardsList />
    </>
  );
};

export default HomeFeed;
